import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormInsertProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import { catchGraphQlError } from '@/lib/catchError';

import UserTenantRoleEntity from '@/entities/userTenantRole';
  import UserEntity from '@/entities/user';
  import TenantEntity from '@/entities/tenant';
  import RoleEntity from '@/entities/role';
  import ProjectEntity from '@/entities/project';

function UserTenantRoleBaseInsertForm({ formOnly = false, onClose, Entity = UserTenantRoleEntity, ...props }: BaseFormInsertProps<typeof UserTenantRoleEntity.model>) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [createMutation, { loading }] = UserTenantRoleEntity.model.useCreate();

  const Component = formOnly ? EntityForm : EntityFormPage;

  const relationsOptionsQuery = UserTenantRoleEntity.model.useRelationsOptions({
    variables: {
          withUsers: true,
          withTenants: true,
          withRoles: true,
          withProjects: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              UserEntity,
              TenantEntity,
              RoleEntity,
              ProjectEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (data) => {
    data.variables = getGraphqlData(Entity, data.variables, 'insert');

    createMutation(data)
      .then((result) => {
        Notistack.toast(t('crud:createdItem', { item: t('entities:userTenantRole') }), { variant: 'success' });


        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.createUserTenantRole);
      })
      .catch(catchGraphQlError);
  };

  if (relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="insert"
      Entity={Entity}
      loading={loading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
    />
  );
}

export default UserTenantRoleBaseInsertForm;
